import { inject } from '@angular/core';
import { SubscriptionStore } from './subscription-store';
import { first, map, of, switchMap } from 'rxjs';
import { AuthUser, PinnedBranch } from '@tvd/data-access/auth';
import { type CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import type { User } from '@shared/models/user';
import { hasRole } from '@shared/auth/has-role';
import { environment } from '@env';

export const subscriptionGuard: CanActivateFn = (_route, state) => {
  // Dormancy gate: until purchases are enabled, the paywall is a no-op.
  // This lets the widened guard (now also on the dashboard route) merge
  // to main and ride to staging/prod harmlessly until TRIAL-007 flips
  // environment.prod.ts.
  if (!environment.enablePurchases) {
    return of(true);
  }

  // Loop break: the billing destination is itself nested under /dashboard,
  // which this guard guards. Without this check, Angular re-evaluates the
  // guard on the redirect target, sees needsPayment is still true, and
  // redirects again forever — eventually giving up to /error/404. If the
  // user is already heading to the billing page, let them through.
  if (state.url.includes('/settings/billing')) {
    return of(true);
  }

  const subscriptionStore = inject(SubscriptionStore);
  const router = inject(Router);
  const authUser = inject(AuthUser);
  const pinnedBranch = inject(PinnedBranch);

  return toObservable(authUser.data).pipe(
    first((data): data is User => !!data),
    switchMap((data) => {
      if (!hasRole(data, 'owner')) {
        return of(true);
      }
      return subscriptionStore.checkPaymentRequirement().pipe(
        map(() => {
          const paymentRequirement = subscriptionStore.paymentRequirement();
          if (!paymentRequirement?.needsPayment) {
            return true;
          }

          // Billing is nested under /dashboard/{orgId}/{branchId}/settings.
          // Prefer the pinned ids; fall back to the user's first role. If we
          // still can't resolve a real billing URL, fail open (the trial
          // banner + billing page itself will surface the paywall) rather
          // than redirecting to /dashboard, which would re-fire this guard
          // and loop.
          const role = data.roles[0];
          const org = pinnedBranch.org() ?? role?.organisation;
          const branch =
            pinnedBranch.branch() ?? role?.shop ?? role?.shops?.[0];
          if (!org || !branch) {
            return true;
          }

          return router.createUrlTree([
            '/dashboard',
            org,
            branch,
            'settings',
            'billing',
          ]);
        }),
      );
    }),
  );
};
